import React, { useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import { PaperWrapper } from 'atoms/PaperWrapper/PaperWrapper';
import Typography from '@mui/material/Typography';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import Loading from '../molcules/Loading/Loading';
import { store } from '../store';
import { getPresence, setPresenceForUser } from './api';
import { hasScope } from '../helpers/scopes';
import { isSickLeaveTag } from '../helpers/shiftTags';
import UpcomingEvents from './UpcomingEvents';
import WeekPlanner from './WeekPlanner';
import PresenceShiftTimes from './PresenceShiftTimes';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { getPresenceHistory } from './api';
import { addDays, format } from 'date-fns';
import { StackedChart } from '../charts/StackedChart/StackedChart';

const STATUS_CONFIG = {
  'on-site': { label: 'On-Site', color: '#1866b8', icon: <LocationOnIcon /> },
  'off-site': { label: 'Working Off-Site', color: '#2e7d32', icon: <LocationOffIcon /> },
  'unavailable': { label: 'Unavailable', color: '#999898', icon: <BlockIcon /> }
};
const memberTags = member => [...new Set([
  ...(member.shifts || []).flatMap(shift => Array.isArray(shift.tags) ? shift.tags.filter(tag => typeof tag === 'string' && tag.trim()) : []),
  ...(member.absences || []).map(absence => absence.type === 'sick-leave' ? 'Sick' : absence.label || 'Leave')
])];

const Container = styled('div')({
  padding: '20px 0px'
});

const StaffGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: 16,
  marginTop: 16
});

const StaffCard = styled(Paper)(({ statuscolor }) => ({
  padding: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  borderLeft: `4px solid ${statuscolor || '#ccc'}`
}));

const StaffInfo = styled('div')({
  flex: 1
});

const NoteText = styled(Typography)({
  fontSize: '0.85rem',
  color: '#666',
  marginTop: 8,
  fontStyle: 'italic'
});

export default function Presence() {
  const { state } = useContext(store);
  const queryClient = useQueryClient();

  const siteId = state.site?._id;
  const canSetPersonalPresence = state.user?.role !== 'site-console';

  const { isLoading = true, data, error } = useQuery({
    queryKey: ['presence', siteId],
    queryFn: () => getPresence(siteId),
    enabled: !!siteId,
    refetchOnWindowFocus: true,
    refetchInterval: 30000
  });

  const seenPeople = new Set();
  const presenceList = (data?.data || []).filter(member => {
    const personId = member.userId?._id || (typeof member.userId === 'string' ? member.userId : null);
    if (!personId) return true;
    const key = String(personId);
    if (seenPeople.has(key)) return false;
    seenPeople.add(key);
    return true;
  }).map(member => ({...member, shifts: Array.isArray(member.shifts) ? member.shifts.slice(0, 1) : []}));
  const today = format(new Date(), 'yyyy-MM-dd');
  const sevenDaysTime = format(addDays(new Date(), 6), 'yyyy-MM-dd');

  const { isLoading: historyLoading, data: historyData } = useQuery({
    queryKey: ['presenceHistory', siteId, today, sevenDaysTime],
    queryFn: async () => {
      return await getPresenceHistory(siteId, today, sevenDaysTime);
    },
    enabled: !!siteId
  });

  if (error) return <p>An error has occurred: {error.message}</p>;

  const grouped = {
    'on-site': presenceList.filter(p => p.status === 'on-site'),
    'off-site': presenceList.filter(p => p.status === 'off-site'),
    'unavailable': presenceList.filter(p => p.status === 'unavailable')
  };

  // Process historyData to count statuses per day
  let statusCountsPerDay = {};
  if (historyData && Array.isArray(historyData.data)) {
    historyData.data.forEach(row => {
      if (!statusCountsPerDay[row.date]) {
        statusCountsPerDay[row.date] = { 'on-site': 0, 'off-site': 0, 'unavailable': 0 };
      }
      if (row.status && statusCountsPerDay[row.date][row.status] !== undefined) {
        statusCountsPerDay[row.date][row.status] += 1;
      }
    });
  }
  // Prepare stacked chart data
  const stackedChartData = Object.keys(statusCountsPerDay)
    .sort((a, b) => a.localeCompare(b))
    .map(date => ({
      date: format(new Date(date), 'EEE do'),
      ...statusCountsPerDay[date]
    }));

  return (
    <Container>

      {isLoading && <Loading />}

      {!isLoading && (
        <Grid container spacing={2} alignItems={"stretch"}>
          {canSetPersonalPresence && <Grid
            size={{
              md: 12,
              sm: 12,
              xs: 12
            }}>
            <WeekPlanner />
          </Grid>}

          <Grid
            size={{
              lg: 6,
              md: 12,
              sm: 12,
              xs: 12
            }}>
            <PaperWrapper heading={`Our Team Today(${presenceList.length})`} loading={false} scroll={true} maxHeight={511}>
              {Object.entries(grouped).map(([status, members]) => (
                members.length > 0 && (
                  <div key={status} style={{ marginBottom: 20 }}>
                    <Typography variant="subtitle2" sx={{ color: STATUS_CONFIG[status].color, mb: 1 }}>
                      {STATUS_CONFIG[status].label} ({members.length})
                    </Typography>
                    <StaffGrid>
                      {members.map(member => {
                        const tags = memberTags(member);
                        const displayedTags = [...new Set(tags.map(tag => isSickLeaveTag(tag) ? 'Sick' : tag))];
                        const sickLeave = tags.some(isSickLeaveTag) || (member.absences || []).some(absence => absence.type === 'sick-leave');
                        const statusColor = sickLeave ? '#f9a825' : STATUS_CONFIG[status].color;
                        return <StaffCard key={member._id} statuscolor={statusColor} elevation={1}>
                          <Avatar sx={{ bgcolor: statusColor, color: sickLeave ? '#6b4f00' : undefined, width: 34, height: 34 }} variant="rounded">
                            {(member.userId?.firstName?.[0] || '').toUpperCase()}
                          </Avatar>
                          <StaffInfo>
                            <Typography variant="body1">
                              {member.userId?.firstName} {member.userId?.lastName}
                            </Typography>

                            <PresenceShiftTimes shifts={member.shifts} />
                            {tags.length > 0 && <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5}}>
                              {displayedTags.map(tag => <Chip key={tag} label={tag} size="small"
                                sx={isSickLeaveTag(tag) || (sickLeave && tag === 'Sick') ? {bgcolor: '#fff3c4', color: '#6b4f00', border: '1px solid #f9a825'} : undefined} />)}
                            </Box>}

                            {(member.userId?.association) && <Typography variant="caption" sx={{ padding: '2px 0 0 0', borderRadius: '4px' }}>{member.userId.association}</Typography>}

                            {member.note && <NoteText>{member.note}</NoteText>}
                          </StaffInfo>
                          {hasScope(state.user, 'presence:manage') && (
                            <Select
                              value={member.status}
                              onChange={async (e) => {
                                const targetUserId = member.userId?._id || member.userId;
                                await setPresenceForUser(siteId, targetUserId, { status: e.target.value, note: member.note || '' });
                                queryClient.invalidateQueries(['presence', siteId]);
                                queryClient.invalidateQueries(['presenceWeek', siteId]);
                              }}
                              size="small"
                              variant="standard"
                              disableUnderline
                              IconComponent={() => null}
                              renderValue={() => <EditIcon sx={{ fontSize: 16, color: '#999', cursor: 'pointer' }} />}
                              sx={{ minWidth: 0, width: 28, '& .MuiSelect-select': { padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                            >
                              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                <MenuItem key={key} value={key} sx={{ fontSize: '0.75rem', gap: 1 }}>
                                  {cfg.icon} {cfg.label}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        </StaffCard>;
                      })}
                    </StaffGrid>
                  </div>
                )
              ))}

              {presenceList.length === 0 && (
                <Typography color="text.secondary">
                  No one has set their presence yet. Be the first!
                </Typography>
              )}
            </PaperWrapper>
          </Grid>

          <Grid
            size={{
              lg: 6,
              md: 12,
              sm: 12,
              xs: 12
            }}>
            <UpcomingEvents />
          </Grid>

          <Grid
            size={{
              md: 12,
              sm: 12,
              xs: 12
            }}>
            <PaperWrapper heading={'Our Team This Week'} loading={historyLoading}>
              {!historyLoading && historyData && historyData.data && (
                <div style={{overflowX:'auto'}}>
                  <table style={{borderCollapse:'collapse', marginBottom:'38px', minWidth:'600px', width:'100%', fontSize:'0.8rem'}}>
                    <thead>
                      <tr>
                        <th style={{textAlign:'left', padding:'6px', borderBottom:'1px solid #eee'}}></th>
                        {Object.keys(statusCountsPerDay).sort((a,b)=>a.localeCompare(b)).map(date => (
                          <th key={date} style={{textAlign:'center', padding:'6px', textTransform:'uppercase', borderBottom:'1px solid #eee'}}>{format(new Date(date), 'EEE do')}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Collect unique staff
                        const staffMap = {};
                        historyData.data.forEach(row => {
                          const id = row.userId?._id || row.userId;
                          if (!staffMap[id]) {
                            staffMap[id] = {
                              name: row.userId?.firstName + ' ' + row.userId?.lastName,
                              statuses: {}
                            };
                          }
                          staffMap[id].statuses[row.date] = row.status;
                        });
                        return Object.values(staffMap).map((staff, idx) => (
                          <tr key={idx}>
                            <td style={{padding:'6px', borderBottom:'1px solid #eee', fontWeight:600}}>{staff.name}</td>
                            {Object.keys(statusCountsPerDay).sort((a,b)=>a.localeCompare(b)).map(date => (
                              <td key={date} style={{textAlign:'center', padding:'6px', borderBottom:'1px solid #eee'}}>
                                {staff.statuses[date] ? (
                                  <span style={{color: STATUS_CONFIG[staff.statuses[date]]?.color || '#333', fontWeight:500}}>
                                    {STATUS_CONFIG[staff.statuses[date]]?.label || staff.statuses[date]}
                                  </span>
                                ) : (
                                  <span style={{color:'#ccc'}}>–</span>
                                )}
                              </td>
                            ))}
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
              {!historyLoading && stackedChartData.length > 0 && (
                <StackedChart data={stackedChartData} index="date" options={{
                  unit: '',
                  colors: [
                    STATUS_CONFIG['on-site'].color,
                    STATUS_CONFIG['off-site'].color,
                    STATUS_CONFIG['unavailable'].color
                  ],
                  noSort: true
                }} />
              )}
              {historyLoading && <Loading />}
            </PaperWrapper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}
